import { Link } from "react-router-dom";
import type { Anime } from "@/types/content";
import { formatNumber } from "@/utils/content";

interface AnimeCardProps {
  anime: Anime;
  rank?: number;
  showPopularity?: boolean;
}

export default function AnimeCard({ anime, rank, showPopularity = false }: AnimeCardProps) {
  return (
    <Link
      to={`/detail/${anime.id}`}
      className="group block w-full cursor-pointer rounded-lg border border-background-200/80 bg-background-50 p-2 transition-colors hover:border-primary-300"
    >
      <div className="relative aspect-[3/4] w-full overflow-hidden rounded-md bg-background-200">
        <img
          src={anime.cover}
          alt={`${anime.title} 动漫封面`}
          title={`${anime.title} 动漫封面`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-foreground-950/80 to-transparent" />
        <span className="absolute left-2 top-2 flex items-center gap-1 rounded-md bg-background-50/95 px-2 py-1 text-[11px] font-semibold text-primary-700">
          <i className="ri-star-fill text-[11px] text-primary-500" />
          {anime.rating.toFixed(1)}
        </span>
        {typeof rank === "number" && (
          <span
            className={[
              "absolute right-2 top-2 flex h-7 min-w-7 items-center justify-center rounded-md px-1.5 font-heading text-xs font-bold",
              rank <= 3
                ? "bg-primary-500 text-background-50"
                : "bg-background-50/90 text-foreground-800",
            ].join(" ")}
          >
            {String(rank).padStart(2, "0")}
          </span>
        )}
        <div className="absolute inset-x-0 bottom-0 flex items-center justify-between px-2.5 pb-2 text-[11px] text-background-100">
          <span className="flex items-center gap-1">
            <i className="ri-play-list-2-line" />
            {anime.status} · {anime.episodes} 集
          </span>
          {showPopularity && (
            <span className="flex items-center gap-1">
              <i className="ri-fire-line" />
              {formatNumber(anime.popularity)}
            </span>
          )}
        </div>
      </div>

      <div className="px-1 pb-1 pt-3">
        <h3 className="truncate font-heading text-sm font-bold text-foreground-950 transition-colors group-hover:text-primary-600 md:text-base">
          {anime.title}
        </h3>
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {anime.tags.slice(0, 2).map((tag) => (
            <span
              key={tag}
              className="rounded-full bg-secondary-100 px-2 py-0.5 text-[10px] font-medium text-secondary-700"
            >
              {tag}
            </span>
          ))}
          <span className="text-[10px] text-foreground-400">{anime.region}</span>
        </div>
        <div className="mt-2 flex items-center justify-between text-[11px] text-foreground-500">
          <span>{anime.year} 年</span>
          <span>{anime.updateDate.slice(5)} 更新</span>
        </div>
      </div>
    </Link>
  );
}